import {
    ref,
    computed,
    reactive,
    readonly
} from 'vue'

export type UploadStatus = 'pending' | 'uploading' | 'success' | 'error' | 'canceled'

export interface UploadItem {
    id: number
    fileName: string
    sizeBytes: number
    progress: number
    status: UploadStatus
    error?: string
}

export type UploadHooks = {
    onProgress: (percent: number) => void
    signal: AbortSignal
}

/** Загрузчик одного файла. Должен пробрасывать ошибку — очередь пометит файл как неуспешный. */
export type Uploader = (file: File, hooks: UploadHooks) => Promise<void>

const AUTO_CLOSE_MS = 5000

let instance: ReturnType<typeof createUploadQueue>

/**
 * Глобальная очередь загрузок файлов с прогрессом.
 * Загрузка идёт последовательно; ошибка одного файла не прерывает остальные.
 * Панель (AppUploadQueuePanel) рендерит это состояние и висит в корне приложения.
 */
function createUploadQueue() {
    const items = ref<UploadItem[]>([])
    const visible = ref(false)

    const controllers = new Map<number, AbortController>()
    let nextId = 0
    let autoCloseTimer: ReturnType<typeof setTimeout> | null = null

    const isActive = computed(() =>
        items.value.some(i => i.status === 'pending' || i.status === 'uploading')
    )
    const totalCount = computed(() => items.value.length)
    const doneCount = computed(() =>
        items.value.filter(i => i.status !== 'pending' && i.status !== 'uploading').length
    )
    const errorCount = computed(() =>
        items.value.filter(i => i.status === 'error').length
    )

    const cancelAutoClose = () => {
        if (autoCloseTimer) {
            clearTimeout(autoCloseTimer)
            autoCloseTimer = null
        }
    }

    const close = () => {
        cancelAutoClose()
        // на всякий случай прерываем всё, что ещё в полёте
        controllers.forEach(c => c.abort())
        visible.value = false
        items.value = []
        controllers.clear()
    }

    // запускаем таймер автозакрытия только когда очередь полностью отработала
    const scheduleAutoClose = () => {
        cancelAutoClose()
        if (isActive.value) {
            return
        }
        autoCloseTimer = setTimeout(() => close(), AUTO_CLOSE_MS)
    }

    const pauseAutoClose = () => cancelAutoClose()
    const resumeAutoClose = () => scheduleAutoClose()

    const cancel = (id: number) => {
        const item = items.value.find(i => i.id === id)
        if (!item) {
            return
        }
        if (item.status === 'pending' || item.status === 'uploading') {
            controllers.get(id)?.abort()
            item.status = 'canceled'
        }
        scheduleAutoClose()
    }

    const runItem = async (item: UploadItem, file: File, uploader: Uploader) => {
        if (item.status === 'canceled') {
            return
        }
        const controller = controllers.get(item.id)
        if (!controller) {
            return
        }

        item.status = 'uploading'
        try {
            await uploader(file, {
                onProgress: (p: number) => {
                    if (item.status !== 'uploading') {
                        return
                    }
                    item.progress = Math.max(0, Math.min(100, Math.round(p)))
                },
                signal: controller.signal,
            })
            if ((item.status as UploadStatus) === 'canceled') {
                return
            }
            item.status = 'success'
            item.progress = 100
        } catch (e: any) {
            if (controller.signal.aborted || (item.status as UploadStatus) === 'canceled') {
                item.status = 'canceled'
            } else {
                item.status = 'error'
                item.error = e && e.message ? String(e.message) : undefined
            }
        }
    }

    /**
     * Ставит файлы в очередь и загружает их последовательно через переданный uploader.
     * Не блокирует вызывающий код — можно не await'ить (загрузка идёт в фоне, панель показывает прогресс).
     */
    const enqueue = async (files: File[], uploader: Uploader): Promise<void> => {
        if (!files.length) {
            return
        }
        cancelAutoClose()
        visible.value = true

        const batch: UploadItem[] = files.map(file => {
            const id = ++nextId
            controllers.set(id, new AbortController())
            const item = reactive<UploadItem>({
                id,
                fileName: file.name,
                sizeBytes: file.size,
                progress: 0,
                status: 'pending',
            })
            items.value.push(item)
            return item
        })

        for (let i = 0; i < batch.length; i++) {
            await runItem(batch[i], files[i], uploader)
        }

        scheduleAutoClose()
    }

    return {
        items,
        visible: readonly(visible),
        isActive,
        totalCount,
        doneCount,
        errorCount,
        enqueue,
        cancel,
        close,
        pauseAutoClose,
        resumeAutoClose,
    }
}

export function useUploadQueue() {
    if (!instance) {
        instance = createUploadQueue()
    }
    return instance
}
